import React, { useState, useEffect, useContext, Fragment } from "react";
import { formatNumberWithSpaces } from "../../../../utils/Utils";
import "./CreditSimulatorPreview.css";
import { calculateCreditSimulationApi } from "../../../../api/CreditSimulatorApi";
import AppContext from "../../../../context/AppContext";
import EmcLogo from "../../../../components/atoms/logos/emcLogo/EmcLogo";

function CreditSimulatorPreview({ creditSimulatorId, showPreview, refreshTrigger = 0 }) {
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { createNotification } = useContext(AppContext);
  const rowsPerPage = 15;

  useEffect(() => {
    if (creditSimulatorId && showPreview) {
      fetchPreviewData();
    }
  }, [creditSimulatorId, showPreview, refreshTrigger]);

  async function fetchPreviewData() {
    try {
      setLoading(true);
      const response = await calculateCreditSimulationApi(creditSimulatorId);
      if (response.data && response.data.credit_simulator) {
        setPreviewData(response.data.credit_simulator);
      } else {
        createNotification(
          <>Format de réponse inattendu. L'aperçu ne peut pas être généré.</>,
          "var(--red)",
          "var(--dark-blue)",
        );
      }
    } catch (error) {
      createNotification(
        <>Impossible de générer l'aperçu. Veuillez vérifier vos données.</>,
        "var(--red)",
        "var(--dark-blue)",
      );
      console.error("Erreur lors du chargement de l'aperçu:", error);
    } finally {
      setLoading(false);
    }
  }

  if (!showPreview) return null;
  if (loading) return <div className='preview-loader'>Génération de l'aperçu en cours...</div>;
  if (!previewData) return null;

  const PageHeader = () => (
    <div className='credit-preview-header'>
      <EmcLogo dark={true} typeLogo={1} />
      <div>
        <span className='credit-preview-title-main'>Simulateur de crédit</span>
        <span className='credit-preview-title-sub'>Tableau d'amortissement</span>
        <hr />
      </div>
    </div>
  );

  const Paginator = ({ currentPage, totalPages }) => (
    <div className='credit-preview-paginator'>
      <div>
        Page {currentPage} / {totalPages}
      </div>
      <hr />
    </div>
  );

  const totalPages = Math.ceil(previewData.rows.length / rowsPerPage) + 1;

  return (
    <div className='credit-preview-container'>
      <div className='credit-preview-page'>
        <PageHeader />
        <h1>Paramètres du crédit</h1>
        <hr className='credit-title-hr' />
        <div className='credit-preview-parameters'>
          <div className='credit-preview-parameters-labels'>
            <span>Montant emprunté :</span>
            <span>Durée du prêt :</span>
            <span>Taux du crédit :</span>
            <span>Taux d'assurance :</span>
            <span>Début du crédit :</span>
            <span>Mensualités du crédit :</span>
            <span>Annuité du crédit :</span>
          </div>
          <div>
            <span>{formatNumberWithSpaces(previewData.data.montant_emprunt, true, "€")}</span>
            <span>{previewData.data.duree_pret} mois</span>
            <span>{formatNumberWithSpaces(previewData.data.taux_credit, true, "%")}</span>
            <span>{formatNumberWithSpaces(previewData.data.taux_assurance, true, "%")}</span>
            <span>{previewData.data.debut_credit}</span>
            <span>{formatNumberWithSpaces(previewData.data.mensualite_credit, true, "€")}</span>
            <span>{formatNumberWithSpaces(previewData.data.annuite_credit, true, "€")}</span>
          </div>
        </div>
        <Paginator currentPage={1} totalPages={totalPages} />
      </div>

      {previewData.rows.map((_, index) => {
        return index % rowsPerPage !== 0 ? (
          <Fragment key={index}></Fragment>
        ) : (
          <div className='credit-preview-page' key={index}>
            <PageHeader />
            <table className='credit-preview-table'>
              <thead>
                <tr>
                  <th>échéance</th>
                  <th>date</th>
                  <th>capital restant</th>
                  <th>mensualité</th>
                  <th>dont capital</th>
                  <th>dont intérêts</th>
                  <th>assurance</th>
                  <th>total ass. incl</th>
                </tr>
              </thead>
              <tbody>
                {previewData.rows.slice(index, index + rowsPerPage).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td>{formatNumberWithSpaces(row.echeance, true)}</td>
                    <td>{row.date}</td>
                    <td>{formatNumberWithSpaces(row.capital_restant, true, "€")}</td>
                    <td>{formatNumberWithSpaces(row.mensualite, true, "€")}</td>
                    <td>{formatNumberWithSpaces(row.mensualite_capital, true, "€")}</td>
                    <td>{formatNumberWithSpaces(row.mensualite_interet, true, "€")}</td>
                    <td>{formatNumberWithSpaces(row.assurance, true, "€")}</td>
                    <td>{formatNumberWithSpaces(row.total_assurance_inclus, true, "€")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginator currentPage={Math.floor(index / rowsPerPage) + 2} totalPages={totalPages} />
          </div>
        );
      })}
    </div>
  );
}

export default CreditSimulatorPreview;
